import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Chip, CircularProgress, Typography} from '@mui/material';

import {workforceDepartmentChipSx} from '../WorkforceProfiles/workforceDepartmentChipSx';

import {ProfileTabButton} from './ProfileTabButton';
import {WorkforceProfileAlignmentAssessmentScopeNote} from './WorkforceProfileAlignmentAssessmentScopeNote';
import {WorkforceProfileAnalysisDataInputSection} from './WorkforceProfileAnalysisDataInputSection';
import {WorkforceProfileCareerCoachSection} from './WorkforceProfileCareerCoachSection';
import {WorkforceProfileCulturalFitAnalysisSection} from './WorkforceProfileCulturalFitAnalysisSection';
import {WorkforceProfileGapsAndSignalsSection} from './WorkforceProfileGapsAndSignalsSection';
import {WorkforceProfileHighlightList} from './WorkforceProfileHighlightList';
import {WorkforceProfileInferredCulturalSection} from './WorkforceProfileInferredCulturalSection';
import {WorkforceProfileProcessingErrorBanner} from './WorkforceProfileProcessingErrorBanner';
import {WorkforceProfileRecommendationCallout} from './WorkforceProfileRecommendationCallout';
import {WorkforceProfileSeniorityLevelAssessmentCard} from './WorkforceProfileSeniorityLevelAssessmentCard';
import {SkillScoreDimension, WorkforceProfileSkillScoreBreakdown} from './WorkforceProfileSkillScoreBreakdown';
import {WorkforceProfileSkillsHub} from './WorkforceProfileSkillsHub';
import {DragAndDropDocumentUpload} from '../../../../../components/DragAndDropDocumentUpload/DragAndDropDocumentUpload';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../../components/EmpionDialog/EmpionDialog';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {Loading} from '../../../../../components/Loading/Loading';
import {ScoreCircle} from '../../../../../components/ScoreCircle/ScoreCircle';
import {TableRowScorePill} from '../../../../../components/TableRowScorePill/TableRowScorePill';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {orangeColors, alignmentStatus, scorePill} from '../../../../../layouts/theme/designSystem';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {capitalizeFirstLetter} from '../../../../../utils/capitalizeFirstLetter';
import {fileToBase64} from '../../../../../utils/fileToBase64';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import {useDownloadWorkforceProfileAnalysisPdf} from '../../hooks/useDownloadWorkforceProfileAnalysisPdf';
import {mapCompanyWorkforceProfileResponse} from '../../serializers/WorkforceProfileSerializers';
import {CompanyWorkforceProfileDetail} from '../../types/WorkforceProfileTypes';
import {
  buildNewlyMetKeySet,
  COMPETENCY_EVOLUTION_SECTIONS,
  KNOWLEDGE_EVOLUTION_SECTIONS,
} from '../../utils/WorkforceProfileDetail/workforceVersionEvolutionUtils';
import {getWorkforceDepartmentDisplayName} from '../../utils/WorkforceProfiles/getWorkforceDepartmentDisplayName';
import {getWorkforceProfileDisplayId} from '../../utils/WorkforceProfiles/getWorkforceProfileDisplayId';

type ProfileTab = 'overview' | 'skills' | 'culture' | 'careerCoach' | 'gapsAndSignals' | 'inputData';

const SKILL_SECTION_ORDER: SkillScoreDimension[] = [
  'coreCompetencies',
  'hardSkills',
  'tools',
  'domainKnowledge',
  'softSkills',
  'responsibilities',
];

export interface Props {
  companyId: string;
  profileId: string;
  onBack: () => void;
}

export const WorkforceProfileDetailView = ({companyId, profileId, onBack}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail'});
  const {t: tShared} = useTranslation();
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [activeSkillSection, setActiveSkillSection] = useState<SkillScoreDimension>('coreCompetencies');
  const [isReanalyseOpen, setIsReanalyseOpen] = useState(false);
  const [reanalyseFile, setReanalyseFile] = useState<File | null>(null);
  const pendingReanalyseFeedbackRef = useRef(false);

  const {
    data: profile,
    isLoading,
    isError,
    invalidateCache,
  } = useApiRead<CompanyWorkforceProfileDetail | undefined>({
    path: GetRoutes.companyWorkforceProfile(companyId, profileId),
    camelCaseOverride: mapCompanyWorkforceProfileResponse,
    options: {
      queryKey: ['companyWorkforceProfile', companyId, profileId],
      enabled: !!companyId && !!profileId,
    },
  });

  const {makeRequest: submitReanalyse, isLoading: isReanalysing} = useApiWrite<
    Post.ReanalyseCompanyWorkforceProfile,
    {companyWorkforceProfile: {profileDocument: string; seniority?: string}}
  >({
    method: 'post',
    path: PostRoutes[Post.ReanalyseCompanyWorkforceProfile](companyId, profileId),
    onSuccess: () => {
      setIsReanalyseOpen(false);
      setReanalyseFile(null);
      pendingReanalyseFeedbackRef.current = true;
      invalidateCache();
    },
  });

  const handleReanalyse = useCallback(async () => {
    if (!reanalyseFile) return;
    const base64 = await fileToBase64(reanalyseFile);
    submitReanalyse({
      companyWorkforceProfile: {profileDocument: base64},
    });
  }, [reanalyseFile, submitReanalyse]);

  const canReanalyse = profile?.status === 'completed' || profile?.status === 'failed';

  useEffect(() => {
    if (isError) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [dispatchMessage, isError, tShared]);

  useEffect(() => {
    if (!pendingReanalyseFeedbackRef.current || !profile) {
      return;
    }

    if (profile.status === 'pending' || profile.status === 'anonymizing') {
      return;
    }

    pendingReanalyseFeedbackRef.current = false;
    const processingError = profile.processingError?.trim();

    if (processingError) {
      dispatchMessage(processingError, 'error');
      return;
    }

    dispatchMessage(t('reanalyse.success'), 'success');
  }, [profile, dispatchMessage, t]);

  const displayId = profile
    ? getWorkforceProfileDisplayId(profile)
    : getWorkforceProfileDisplayId({resourceId: profileId});
  const canDownloadAnalysisPdf = profile?.status === 'completed' && !!profile?.analyzedAt;
  const analysisPdfFileName = `${displayId.replace(/[^a-zA-Z0-9_-]+/g, '_')}_analysis.pdf`;

  const {downloadPdf: downloadAnalysisPdf, isLoading: isAnalysisPdfDownloading} =
    useDownloadWorkforceProfileAnalysisPdf({
      companyId,
      profileId,
      fileName: analysisPdfFileName,
      onError: () => dispatchMessage(t('downloadAnalysisPdfError'), 'error'),
    });

  const handleDownloadAnalysisPdf = useCallback(async () => {
    await downloadAnalysisPdf();
  }, [downloadAnalysisPdf]);

  const resolvedDepartmentLabel = getWorkforceDepartmentDisplayName(profile?.companyDepartment?.name, currentLanguage);
  const targetTitle = profile?.companyDepartmentTargetProfile?.title ?? '';
  const overall = profile?.alignment?.overall;
  const hasAlignment = overall != null && profile?.status === 'completed';

  const hasCoreCompetencies = (profile?.coreCompetencyRows?.length ?? 0) > 0;
  const hasHardSkills = (profile?.hardSkillRows?.length ?? 0) > 0;
  const hasTools = (profile?.toolRows?.length ?? 0) > 0;
  const hasDomainKnowledge = (profile?.domainKnowledgeRows?.length ?? 0) > 0;
  const hasSoftSkills = (profile?.softSkillRows?.length ?? 0) > 0;
  const hasResponsibilities =
    (profile?.responsibilitiesRows?.length ?? 0) > 0 ||
    !!profile?.responsibilitiesExplanation ||
    profile?.alignment?.responsibilities != null;
  const hasSkills =
    hasCoreCompetencies ||
    hasHardSkills ||
    hasTools ||
    hasDomainKnowledge ||
    hasSoftSkills ||
    hasResponsibilities ||
    profile?.alignment?.coreCompetencies != null ||
    profile?.alignment?.hardSkills != null ||
    profile?.alignment?.tools != null ||
    profile?.alignment?.domainKnowledge != null ||
    profile?.alignment?.softSkills != null ||
    profile?.alignment?.responsibilities != null;
  const hasCulture =
    !!profile?.culturalFitAnalysis?.available ||
    (profile?.inferredCulturalAnswers?.length ?? 0) > 0 ||
    profile?.alignment?.culturalFit != null;
  const hasInputData = !!profile?.profileAnalysisDataInput;
  const hasCareerCoachTab = profile?.careerCoachAnalysis != null || !!profile?.versionEvolution?.available;

  // Version-evolution "newly met" diffing is still computed by the BE against the legacy
  // v1 section names only (core competencies / hard skills); soft skills have no evolution data yet.
  const newlyMetCoreCompetencyKeys = useMemo(
    () => buildNewlyMetKeySet(profile?.versionEvolution?.newlyMetSkills, COMPETENCY_EVOLUTION_SECTIONS),
    [profile?.versionEvolution?.newlyMetSkills]
  );

  const newlyMetHardSkillKeys = useMemo(
    () => buildNewlyMetKeySet(profile?.versionEvolution?.newlyMetSkills, KNOWLEDGE_EVOLUTION_SECTIONS),
    [profile?.versionEvolution?.newlyMetSkills]
  );

  const newlyMetEvolutionNote = useMemo(() => {
    const evolution = profile?.versionEvolution;
    if (!evolution?.available) {
      return undefined;
    }

    const from = evolution.baselineVersion;
    const to = evolution.currentVersion;
    if (from == null || to == null) {
      return String(t('alignmentOverview.newlyMetAfterEvolutionShort'));
    }

    return String(t('alignmentOverview.newlyMetAfterEvolution', {from, to}));
  }, [profile?.versionEvolution, t]);

  const coreCompetencyGapCount = useMemo(
    () => profile?.coreCompetencyRows?.filter((r) => !r.met).length ?? 0,
    [profile?.coreCompetencyRows]
  );
  const hardSkillGapCount = useMemo(
    () => profile?.hardSkillRows?.filter((r) => !r.met).length ?? 0,
    [profile?.hardSkillRows]
  );
  const toolsGapCount = useMemo(() => profile?.toolRows?.filter((r) => !r.met).length ?? 0, [profile?.toolRows]);
  const domainKnowledgeGapCount = useMemo(
    () => profile?.domainKnowledgeRows?.filter((r) => !r.met).length ?? 0,
    [profile?.domainKnowledgeRows]
  );
  const softSkillGapCount = useMemo(
    () => profile?.softSkillRows?.filter((r) => !r.met).length ?? 0,
    [profile?.softSkillRows]
  );
  const responsibilitiesGapCount = useMemo(
    () => profile?.responsibilitiesRows?.filter((r) => !r.met).length ?? 0,
    [profile?.responsibilitiesRows]
  );
  const highlightGapCount = profile?.highlights?.gaps?.length ?? 0;
  const totalGapCount =
    coreCompetencyGapCount +
    hardSkillGapCount +
    toolsGapCount +
    domainKnowledgeGapCount +
    softSkillGapCount +
    responsibilitiesGapCount +
    highlightGapCount;

  const hasGapsData = hasAlignment && totalGapCount > 0;

  const notMetCoreCompetencies = useMemo(
    () => profile?.coreCompetencyRows?.filter((r) => !r.met) ?? [],
    [profile?.coreCompetencyRows]
  );
  const notMetHardSkills = useMemo(() => profile?.hardSkillRows?.filter((r) => !r.met) ?? [], [profile?.hardSkillRows]);
  const notMetTools = useMemo(() => profile?.toolRows?.filter((r) => !r.met) ?? [], [profile?.toolRows]);
  const notMetDomainKnowledge = useMemo(
    () => profile?.domainKnowledgeRows?.filter((r) => !r.met) ?? [],
    [profile?.domainKnowledgeRows]
  );
  const notMetSoftSkills = useMemo(() => profile?.softSkillRows?.filter((r) => !r.met) ?? [], [profile?.softSkillRows]);
  const notMetResponsibilities = useMemo(
    () => profile?.responsibilitiesRows?.filter((r) => !r.met) ?? [],
    [profile?.responsibilitiesRows]
  );

  // Reset tab if current tab becomes unavailable
  useEffect(() => {
    if (activeTab === 'skills' && !hasSkills) setActiveTab('overview');
    if (activeTab === 'culture' && !hasCulture) setActiveTab('overview');
    if (activeTab === 'careerCoach' && !hasCareerCoachTab) setActiveTab('overview');
    if (activeTab === 'gapsAndSignals' && !hasGapsData) setActiveTab('overview');
    if (activeTab === 'inputData' && !hasInputData) setActiveTab('overview');
  }, [activeTab, hasSkills, hasCulture, hasCareerCoachTab, hasGapsData, hasInputData]);

  const openSkillSection = useCallback((section: SkillScoreDimension) => {
    setActiveSkillSection(section);
    setActiveTab('skills');
  }, []);

  const handleScoreCircleClick = useCallback(
    (dimension: string) => {
      if (dimension === 'overall') {
        setActiveTab('overview');
        return;
      }
      if (dimension === 'culturalFit') {
        if (hasCulture) setActiveTab('culture');
        return;
      }
    },
    [hasCulture]
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!profile) {
    return (
      <Box>
        <EmpionButton variant='secondary' size='small' startIcon={<ArrowBackIcon />} onClick={onBack} sx={{mb: 2}}>
          {t('back')}
        </EmpionButton>
        <Typography color='text.secondary'>{tShared('shared.fetchingData.error')}</Typography>
      </Box>
    );
  }

  const coreCompetencyTotal = profile.coreCompetencyRows.length;
  const coreCompetencyMetCount = coreCompetencyTotal - coreCompetencyGapCount;
  const hardSkillTotal = profile.hardSkillRows.length;
  const hardSkillMetCount = hardSkillTotal - hardSkillGapCount;
  const toolsTotal = profile.toolRows.length;
  const toolsMetCount = toolsTotal - toolsGapCount;
  const domainKnowledgeTotal = profile.domainKnowledgeRows.length;
  const domainKnowledgeMetCount = domainKnowledgeTotal - domainKnowledgeGapCount;
  const softSkillTotal = profile.softSkillRows.length;
  const softSkillMetCount = softSkillTotal - softSkillGapCount;
  const processingError = profile.processingError?.trim() ?? '';
  const seniorityLevelAssessment = profile.seniorityLevelAssessment;

  const skillAvailability: Record<SkillScoreDimension, boolean> = {
    coreCompetencies: hasCoreCompetencies || profile.alignment?.coreCompetencies != null,
    hardSkills: hasHardSkills || profile.alignment?.hardSkills != null,
    tools: hasTools || profile.alignment?.tools != null,
    domainKnowledge: hasDomainKnowledge || profile.alignment?.domainKnowledge != null,
    softSkills: hasSoftSkills || profile.alignment?.softSkills != null,
    responsibilities: hasResponsibilities,
  };

  const skillBreakdownItems = [
    {
      key: 'coreCompetencies' as const,
      label: t('scores.coreCompetencies'),
      score: profile.alignment?.coreCompetencies,
      metCount: coreCompetencyMetCount,
      totalCount: coreCompetencyTotal,
      available: skillAvailability.coreCompetencies,
    },
    {
      key: 'hardSkills' as const,
      label: t('scores.hardSkills'),
      score: profile.alignment?.hardSkills,
      metCount: hardSkillMetCount,
      totalCount: hardSkillTotal,
      available: skillAvailability.hardSkills,
    },
    {
      key: 'tools' as const,
      label: t('scores.tools'),
      score: profile.alignment?.tools,
      metCount: toolsMetCount,
      totalCount: toolsTotal,
      available: skillAvailability.tools,
    },
    {
      key: 'domainKnowledge' as const,
      label: t('scores.domainKnowledge'),
      score: profile.alignment?.domainKnowledge,
      metCount: domainKnowledgeMetCount,
      totalCount: domainKnowledgeTotal,
      available: skillAvailability.domainKnowledge,
    },
    {
      key: 'softSkills' as const,
      label: t('scores.softSkills'),
      score: profile.alignment?.softSkills,
      metCount: softSkillMetCount,
      totalCount: softSkillTotal,
      available: skillAvailability.softSkills,
    },
    {
      key: 'responsibilities' as const,
      label: t('scores.responsibilities'),
      score: profile.alignment?.responsibilities,
      metCount: profile.responsibilitiesRows.length - responsibilitiesGapCount,
      totalCount: profile.responsibilitiesRows.length,
      available: skillAvailability.responsibilities,
    },
  ];

  const skillHubSections = [
    {
      key: 'coreCompetencies' as const,
      label: t('tabs.coreCompetencies'),
      title: t('coreCompetenciesTitle'),
      available: skillAvailability.coreCompetencies,
      rows: profile.coreCompetencyRows,
      newlyMetKeys: newlyMetCoreCompetencyKeys,
      newlyMetNote: newlyMetEvolutionNote,
    },
    {
      key: 'hardSkills' as const,
      label: t('tabs.hardSkills'),
      title: t('hardSkillsTitle'),
      available: skillAvailability.hardSkills,
      rows: profile.hardSkillRows,
      newlyMetKeys: newlyMetHardSkillKeys,
      newlyMetNote: newlyMetEvolutionNote,
    },
    {
      key: 'tools' as const,
      label: t('tabs.tools'),
      title: t('toolsTitle'),
      available: skillAvailability.tools,
      rows: profile.toolRows,
    },
    {
      key: 'domainKnowledge' as const,
      label: t('tabs.domainKnowledge'),
      title: t('domainKnowledgeTitle'),
      available: skillAvailability.domainKnowledge,
      rows: profile.domainKnowledgeRows,
    },
    {
      key: 'softSkills' as const,
      label: t('tabs.softSkills'),
      title: t('softSkillsTitle'),
      available: skillAvailability.softSkills,
      rows: profile.softSkillRows,
    },
    {
      key: 'responsibilities' as const,
      label: t('tabs.responsibilities'),
      title: t('responsibilitiesTitle'),
      available: skillAvailability.responsibilities,
      rows: profile.responsibilitiesRows,
      emptyExplanation: profile.responsibilitiesExplanation,
    },
  ];

  const resolvedSkillSection =
    SKILL_SECTION_ORDER.find((key) => key === activeSkillSection && skillAvailability[key]) ??
    SKILL_SECTION_ORDER.find((key) => skillAvailability[key]) ??
    'coreCompetencies';

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
        <EmpionButton variant='secondary' size='small' startIcon={<ArrowBackIcon />} onClick={onBack}>
          {t('back')}
        </EmpionButton>
      </Box>

      {processingError && <WorkforceProfileProcessingErrorBanner message={processingError} />}

      {/* --- Profile card: header, tabs, and tab content --- */}
      <EmpionPaper
        sx={{
          border: `1px solid ${orangeColors[3]}`,
          borderRadius: '14px',
          p: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{px: 3, pt: 2.5, pb: hasAlignment ? 0 : 2.5}}>
          <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start'}}>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography sx={{fontSize: 18, fontWeight: 600, letterSpacing: '-0.3px'}}>{displayId}</Typography>
              <Box sx={{mt: 0.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75}}>
                <Chip label={resolvedDepartmentLabel} size='small' sx={workforceDepartmentChipSx} />
                <Typography variant='body2' color='text.secondary'>
                  ·
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {getSeniorityLevelLabel(profile.seniority, tShared)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  .
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {t('targetLabel')}: <strong>{capitalizeFirstLetter(targetTitle)}</strong>
                </Typography>
              </Box>
              {(canDownloadAnalysisPdf || canReanalyse) && (
                <Box sx={{mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  {canDownloadAnalysisPdf && (
                    <EmpionButton
                      variant='cta'
                      size='small'
                      onClick={handleDownloadAnalysisPdf}
                      disabled={isAnalysisPdfDownloading}
                      startIcon={
                        isAnalysisPdfDownloading ? (
                          <CircularProgress size={16} sx={{color: 'white'}} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                    >
                      {t('downloadAnalysisPdf')}
                    </EmpionButton>
                  )}
                  {canReanalyse && (
                    <EmpionButton
                      variant='secondary'
                      size='small'
                      startIcon={<RefreshIcon />}
                      onClick={() => setIsReanalyseOpen(true)}
                    >
                      {t('reanalyse.button')}
                    </EmpionButton>
                  )}
                </Box>
              )}
              {!hasAlignment && (
                <Typography variant='body2' color='text.secondary' sx={{mt: 1}}>
                  {t('analysisPending')}
                </Typography>
              )}
            </Box>
            {hasAlignment && profile.alignment && (
              <Box sx={{display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'flex-start'}}>
                {profile.alignment.overall != null && (
                  <ScoreCircle
                    value={profile.alignment.overall}
                    label={t('scores.overall')}
                    onClick={() => handleScoreCircleClick('overall')}
                    active={activeTab === 'overview'}
                  />
                )}
                {profile.alignment.culturalFit != null && (
                  <ScoreCircle
                    value={profile.alignment.culturalFit}
                    label={t('scores.culturalFit')}
                    onClick={hasCulture ? () => handleScoreCircleClick('culturalFit') : undefined}
                    active={activeTab === 'culture'}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Tab bar */}
        {hasAlignment && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0,
              borderTop: `1px solid ${orangeColors[3]}`,
              mt: 1,
              px: 1,
            }}
          >
            <ProfileTabButton
              label={t('tabs.overview')}
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            />
            {hasSkills && (
              <ProfileTabButton
                label={t('tabs.skills')}
                active={activeTab === 'skills'}
                onClick={() => setActiveTab('skills')}
              />
            )}
            {hasCulture && (
              <ProfileTabButton
                label={t('tabs.culture')}
                active={activeTab === 'culture'}
                onClick={() => setActiveTab('culture')}
              />
            )}
            {hasCareerCoachTab && (
              <ProfileTabButton
                label={t('tabs.careerCoach')}
                active={activeTab === 'careerCoach'}
                onClick={() => setActiveTab('careerCoach')}
              />
            )}
            {hasGapsData && (
              <ProfileTabButton
                label={t('tabs.gapsAndSignals')}
                active={activeTab === 'gapsAndSignals'}
                onClick={() => setActiveTab('gapsAndSignals')}
                count={totalGapCount}
                countVariant='warning'
              />
            )}
            {hasInputData && (
              <ProfileTabButton
                label={t('tabs.inputData')}
                active={activeTab === 'inputData'}
                onClick={() => setActiveTab('inputData')}
              />
            )}
          </Box>
        )}

        {/* --- Tab content --- */}
        {hasAlignment && overall != null && (
          <Box sx={{px: 3, py: 2.5, borderTop: `1px solid ${orangeColors[3]}`}}>
            {/* ======= OVERVIEW TAB ======= */}
            {activeTab === 'overview' && (
              <Box>
                <WorkforceProfileRecommendationCallout overall={overall} gapCount={totalGapCount} />

                {seniorityLevelAssessment && (
                  <WorkforceProfileSeniorityLevelAssessmentCard assessment={seniorityLevelAssessment} />
                )}

                {hasSkills && (
                  <WorkforceProfileSkillScoreBreakdown
                    title={t('skillBreakdownTitle')}
                    items={skillBreakdownItems}
                    onSelect={openSkillSection}
                  />
                )}

                {profile.summary && (
                  <EmpionPaper sx={{border: `1px solid ${orangeColors[3]}`, borderRadius: '14px', p: 2.5, mb: 2}}>
                    <Typography variant='h3' sx={{fontWeight: 600, mb: 1}}>
                      {t('summaryTitle')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.65}}>
                      {profile.summary}
                    </Typography>
                  </EmpionPaper>
                )}

                {profile.highlights && (
                  <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                    <WorkforceProfileHighlightList
                      title={t('strongAlignment')}
                      items={profile.highlights.strong}
                      variant='strong'
                    />
                    <WorkforceProfileHighlightList
                      title={t('partialGaps')}
                      items={profile.highlights.gaps}
                      variant='partial'
                      footer={
                        hasGapsData ? (
                          <EmpionButton
                            variant='secondary'
                            size='small'
                            onClick={() => setActiveTab('gapsAndSignals')}
                            sx={{mt: 1.5, width: '100%', justifyContent: 'center'}}
                          >
                            {t('viewAllGaps')}
                          </EmpionButton>
                        ) : undefined
                      }
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* ======= SKILLS HUB ======= */}
            {activeTab === 'skills' && hasSkills && (
              <Box>
                {profile.targetProfileAlignmentAssessmentScope && (
                  <WorkforceProfileAlignmentAssessmentScopeNote
                    assessmentScope={profile.targetProfileAlignmentAssessmentScope}
                  />
                )}
                <WorkforceProfileSkillsHub
                  sections={skillHubSections}
                  activeSection={resolvedSkillSection}
                  onSectionChange={setActiveSkillSection}
                />
              </Box>
            )}

            {/* ======= CULTURE TAB ======= */}
            {activeTab === 'culture' && hasCulture && (
              <Box>
                {profile.culturalFitAnalysis?.available && (
                  <WorkforceProfileCulturalFitAnalysisSection analysis={profile.culturalFitAnalysis} alwaysExpanded />
                )}
                {!profile.culturalFitAnalysis?.available &&
                  profile.inferredCulturalAnswers &&
                  profile.inferredCulturalAnswers.length > 0 && (
                  <WorkforceProfileInferredCulturalSection answers={profile.inferredCulturalAnswers} alwaysExpanded />
                )}
                {!profile.culturalFitAnalysis?.available &&
                  (!profile.inferredCulturalAnswers || profile.inferredCulturalAnswers.length === 0) &&
                  profile.alignment?.culturalFit != null && (
                  <EmpionPaper
                    sx={{
                      border: `1px solid ${orangeColors[3]}`,
                      borderRadius: '14px',
                      p: 2.5,
                    }}
                  >
                    <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1}}>
                      {t('scores.culturalFit')}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 99,
                          bgcolor: orangeColors[5],
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${profile.alignment.culturalFit}%`,
                            borderRadius: 99,
                            bgcolor:
                                profile.alignment.culturalFit >= 70
                                  ? alignmentStatus.metAccent
                                  : scorePill.medium.strokeColor,
                          }}
                        />
                      </Box>
                      <Typography variant='body2' sx={{fontWeight: 600}}>
                        {profile.alignment.culturalFit}%
                      </Typography>
                    </Box>
                  </EmpionPaper>
                )}
              </Box>
            )}

            {/* ======= GAPS & SIGNALS TAB ======= */}
            {activeTab === 'gapsAndSignals' && hasGapsData && profile && overall != null && (
              <WorkforceProfileGapsAndSignalsSection
                profile={profile}
                overall={overall}
                totalGapCount={totalGapCount}
                notMetCoreCompetencies={notMetCoreCompetencies}
                notMetHardSkills={notMetHardSkills}
                notMetTools={notMetTools}
                notMetDomainKnowledge={notMetDomainKnowledge}
                notMetSoftSkills={notMetSoftSkills}
                notMetResponsibilities={notMetResponsibilities}
              />
            )}
            {/* ======= CAREER COACH TAB ======= */}
            {activeTab === 'careerCoach' && hasCareerCoachTab && (
              <WorkforceProfileCareerCoachSection
                analysis={profile.careerCoachAnalysis ?? {available: false}}
                versionEvolution={profile.versionEvolution}
              />
            )}

            {/* ======= INPUT DATA TAB ======= */}
            {activeTab === 'inputData' && hasInputData && profile.profileAnalysisDataInput && (
              <WorkforceProfileAnalysisDataInputSection data={profile.profileAnalysisDataInput} alwaysExpanded />
            )}

            {/* Fallback: no alignment but has data */}
            {profile.coreCompetencyRows.length === 0 &&
              profile.hardSkillRows.length === 0 &&
              profile.softSkillRows.length === 0 &&
              activeTab === 'overview' && (
              <Box sx={{mt: 2}}>
                <TableRowScorePill score={overall} />
              </Box>
            )}
          </Box>
        )}
      </EmpionPaper>

      {/* Re-analyse dialog */}
      <EmpionDialog
        open={isReanalyseOpen}
        onClose={() => {
          setIsReanalyseOpen(false);
          setReanalyseFile(null);
        }}
        title={t('reanalyse.dialogTitle')}
        maxWidth='sm'
      >
        <Box sx={{p: 2}}>
          <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
            {t('reanalyse.dialogDescription')}
          </Typography>
          <DragAndDropDocumentUpload
            value={reanalyseFile}
            onChange={(file) => setReanalyseFile(file)}
            accept='application/pdf'
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 2,
            }}
          >
            <EmpionButton
              variant='secondary'
              size='small'
              onClick={() => {
                setIsReanalyseOpen(false);
                setReanalyseFile(null);
              }}
            >
              {tShared('shared.formButtons.cancel')}
            </EmpionButton>
            <EmpionButton
              variant='primary'
              size='small'
              disabled={!reanalyseFile || isReanalysing}
              onClick={handleReanalyse}
            >
              {isReanalysing ? tShared('shared.formButtons.submitting') : t('reanalyse.submit')}
            </EmpionButton>
          </Box>
        </Box>
      </EmpionDialog>
    </Box>
  );
};
